// RITA-1523 (Nutzungserfassung, Versionsvergleich) — Vorgabe und Ordnung der beiden Stände.
//
// CONTRACT: „Vorher" ist IMMER der ältere Stand, „Nachher" der jüngere; die Änderung liest sich als
// Nachher gegen Vorher. Die Ordnung steckt in der ANTWORT, nicht in einer eigenen Rechnung: die
// Lese-API liefert die Stände nach firstSeenDay absteigend und bei gleichem Tag nach Kennung
// aufsteigend (usage.graphql, UsageVersionReport.versions). Daraus folgt beides — die Vorgabe (die
// zwei jüngsten Stände) und der Tausch einer Auswahl gegen die Ordnung.
//
// Ein eigener Datumsvergleich wäre eine zweite Wahrheit: zwei Stände desselben Tages haben denselben
// firstSeenDay, und welcher von ihnen der jüngere ist, entscheidet dann allein die Reihenfolge der
// Antwort. Reine Funktionen, damit genau das einzeln prüfbar bleibt (versionsAuswahl.test.ts).
import type { UsageVersionRow } from '../types';

export interface StandAuswahl {
  /** Kennung des älteren Standes; null, solange keiner gewählt ist. */
  vorher: string | null;
  /** Kennung des jüngeren Standes. */
  nachher: string | null;
}

/** Das Feld, das der Betreiber gerade geändert hat. */
export type AuswahlFeld = 'vorher' | 'nachher';

/**
 * Vorgabe: die zwei jüngsten wählbaren Stände in der Ordnung der Antwort. Mit nur einem Stand
 * bleibt „Vorher" leer — die Oberfläche sagt dann, dass ein Vergleich zwei Stände braucht.
 */
export const vorgabeAuswahl = (versionen: readonly UsageVersionRow[]): StandAuswahl => ({
  vorher: versionen[1]?.appVersion ?? null,
  nachher: versionen[0]?.appVersion ?? null,
});

/** Stelle in der Antwort (0 = jüngster); eine nicht gelistete Kennung gilt als älteste. */
const stelle = (versionen: readonly UsageVersionRow[], kennung: string): number => {
  const gefunden = versionen.findIndex((version) => version.appVersion === kennung);
  return gefunden === -1 ? versionen.length : gefunden;
};

/** Dreht eine Auswahl gegen die Ordnung (Vorher jünger als Nachher) um, statt sie so zu zeigen. */
export const geordnet = (versionen: readonly UsageVersionRow[], auswahl: StandAuswahl): StandAuswahl => {
  const { vorher, nachher } = auswahl;
  if (vorher === null || nachher === null) return auswahl;
  return stelle(versionen, vorher) < stelle(versionen, nachher) ? { vorher: nachher, nachher: vorher } : auswahl;
};

/**
 * Eine Feldänderung. Dieselbe Kennung in beiden Feldern gibt es nicht: der Versuch tauscht die
 * beiden Stände (und wird durch `geordnet` gleich wieder in die Ordnung gebracht, ist also ein
 * sichtbares Nichts statt einer abgelehnten Anfrage — der Server verbietet A = B).
 */
export const auswahlNach = (
  versionen: readonly UsageVersionRow[],
  aktuell: StandAuswahl,
  feld: AuswahlFeld,
  wert: string,
): StandAuswahl => {
  const kennung = wert === '' ? null : wert;
  const roh: StandAuswahl =
    feld === 'vorher'
      ? { vorher: kennung, nachher: kennung !== null && kennung === aktuell.nachher ? aktuell.vorher : aktuell.nachher }
      : { vorher: kennung !== null && kennung === aktuell.vorher ? aktuell.nachher : aktuell.vorher, nachher: kennung };
  return geordnet(versionen, roh);
};
